/**
 * RFC-0088: Yjs document connection and lifecycle hook.
 *
 * Connects to the backend WebSocket endpoint and maintains a Yjs document
 * that syncs bidirectionally with the server's pycrdt document.
 *
 * CRITICAL: A fresh Y.Doc must be created for each projectId/canvasId
 * combination. Reusing a Y.Doc across projects causes cross-project data
 * contamination because the Yjs CRDT sync protocol sends ALL local state
 * to the server on connect — including stale data from a previous project.
 */

import { useEffect, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'

/** Access internal wsLastMessageReceived on WebsocketProvider (not in public types). */
function getWsLastMessageReceived(provider: WebsocketProvider): number | undefined {
  return provider.wsLastMessageReceived
}
function setWsLastMessageReceived(provider: WebsocketProvider, value: number): void {
  provider.wsLastMessageReceived = value
}
import { getConfiguredApiBase, getAuthToken, createDebug, setDebugClientID } from '@ponpon/api-client'
const log = createDebug('dw:ydoc:lifecycle')

declare global {
  interface Window {
    __ydoc?: Y.Doc
    __ydocProvider?: WebsocketProvider
  }
}

interface UseYDocOptions {
  projectId: string
  canvasId?: string
  enabled?: boolean
}

interface UseYDocResult {
  /** The Yjs document (fresh per projectId/canvasId) */
  ydoc: Y.Doc
  /** Whether the initial sync with the server is complete */
  synced: boolean
  /** Whether the WebSocket connection is live */
  connected: boolean
}

/**
 * Convert an HTTP(S) API base URL to a WebSocket URL.
 * e.g. "http://localhost:9000" → "ws://localhost:9000"
 *      "https://api.example.com" → "wss://api.example.com"
 */
function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

/** Liveness watchdog. Lower y-websocket's resyncInterval to drive
 *  inbound traffic every 2s; the watchdog reads provider.wsLastMessageReceived
 *  every 2s and force-closes if the gap exceeds 5s. ~5s detection. */
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_DEAD_MS = 5000

export function useYDoc({
  projectId,
  canvasId = 'agent',
  enabled = true,
}: UseYDocOptions): UseYDocResult {
  // State for the Y.Doc instance, keyed by projectId/canvasId.
  // A fresh Y.Doc is created inside the useEffect below whenever the key
  // changes, and the state is updated to trigger a re-render with the new doc.
  const [ydoc, setYdoc] = useState<Y.Doc>(() => new Y.Doc())
  const [synced, setSynced] = useState(false)
  const [connected, setConnected] = useState(false)

  // Expose YDoc on window for E2E testing (dev mode only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      window.__ydoc = ydoc
    }
    return () => {
      if (import.meta.env.DEV) {
        delete window.__ydoc
      }
    }
  }, [ydoc])

  // Single comprehensive effect: manages both Y.Doc lifecycle and WebSocket.
  // When projectId or canvasId changes, this effect creates a FRESH Y.Doc,
  // connects it to the new room, and destroys the old one on cleanup.
  // This eliminates the race condition where a stale Y.Doc connects to a
  // new project's room and contaminates it with old data.
  useEffect(() => {
    if (!enabled || !projectId) return

    // Create a fresh Y.Doc for this project/canvas combination.
    // This doc has NO data from any previous project.
    const freshDoc = new Y.Doc()
    log('created fresh Y.Doc for %s/%s (clientID=%d)', projectId, canvasId, freshDoc.clientID)

    // Update state so consumers (useYCanvas, etc.) get the new doc
    setYdoc(freshDoc)
    setSynced(false)
    setConnected(false)

    // Build WebSocket URL
    const apiBase = getConfiguredApiBase()
    const wsBase = toWsUrl(apiBase)
    const roomName = `${projectId}/${canvasId}`
    const wsUrl = `${wsBase}/ws/yjs`

    let cancelled = false
    let provider: WebsocketProvider | null = null
    let watchdogTimer: ReturnType<typeof setInterval> | null = null

    const stopWatchdog = () => {
      if (watchdogTimer != null) {
        clearInterval(watchdogTimer)
        watchdogTimer = null
      }
    }

    // When tab returns to foreground, reset wsLastMessageReceived so a
    // long background-throttled gap doesn't immediately trigger a
    // false-positive reconnect. The next sync cycle (every 2s via
    // resyncInterval) will surface a real disconnect within
    // HEARTBEAT_DEAD_MS.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && provider) {
        // y-websocket tracks wsLastMessageReceived in ms (lib0 time.getUnixTime).
        // Reset to "now" so the watchdog has a fresh baseline.
        setWsLastMessageReceived(provider, Date.now())
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const connect = async () => {
      const token = await getAuthToken()
      if (cancelled) return

      const params: Record<string, string> = {}
      if (token) {
        params['token'] = token
      }

      provider = new WebsocketProvider(wsUrl, roomName, freshDoc, {
        connect: true,
        params,
        // Sync messages serve as both real state-sync and as inbound traffic
        // that the watchdog uses for liveness. 2s gives the watchdog (5s
        // timeout) enough samples to detect silent disconnects within ~5s.
        resyncInterval: 2000,
        // Cap above the y-websocket default of 2.5s so a transient blip
        // doesn't hammer the server with too many reconnect attempts.
        maxBackoffTime: 5000,
      })

      provider.on('status', async ({ status }: { status: string }) => {
        if (cancelled) return
        log('ws status=%s room=%s', status, roomName)
        setConnected(status === 'connected')
        if (status === 'disconnected') {
          try {
            const freshToken = await getAuthToken()
            if (freshToken && provider?.params) {
              provider.params['token'] = freshToken
            }
            if (!freshToken) {
              log('auth token null — potential session expiry')
            }
          } catch (err) {
            log('token refresh failed: %O', err)
          }
        }
      })

      provider.on('sync', (isSynced: boolean) => {
        if (cancelled) return
        log('ws synced=%s room=%s', isSynced, roomName)
        setSynced(isSynced)
      })

      setDebugClientID(freshDoc.clientID)

      // Liveness watchdog: y-websocket's onmessage updates
      // wsLastMessageReceived. If no inbound message for HEARTBEAT_DEAD_MS,
      // force-close the underlying ws — y-websocket's onclose handler
      // schedules an automatic reconnect via setupWS.
      stopWatchdog()
      watchdogTimer = setInterval(() => {
        if (cancelled || !provider) return
        const lastMs = getWsLastMessageReceived(provider)
        if (lastMs && Date.now() - lastMs > HEARTBEAT_DEAD_MS) {
          log('ws watchdog-timeout room=%s — forcing reconnect', roomName)
          if (provider.ws) {
            try { provider.ws.close() } catch { /* already closed */ }
          }
        }
      }, HEARTBEAT_INTERVAL_MS)

      // Expose Y.Doc and provider for Playwright tests (dev only)
      if (import.meta.env.DEV) {
        window.__ydoc = freshDoc
        window.__ydocProvider = provider
      }
    }

    connect()

    return () => {
      cancelled = true
      stopWatchdog()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      log('cleanup room=%s (clientID=%d)', roomName, freshDoc.clientID)
      if (import.meta.env.DEV) {
        delete window.__ydocProvider
      }
      if (provider) {
        provider.destroy()
      }
      freshDoc.destroy()
      setConnected(false)
      setSynced(false)
    }
  }, [projectId, canvasId, enabled])

  return { ydoc, synced, connected }
}
